import Image from 'next/image';
import useLocale from 'next-translate/useTranslation';
import type { CSSProperties, ReactNode } from 'react';

import clsxm from '@/lib/clsxm';
import { signupHrefForPath } from '@/lib/presetFromPath';

import {
  type EmployeePhotoKey,
  employeePhotoUrl,
} from '@/constants/employeePhotos';
import { useUser } from '@/utils/useUser';

/**
 * The page's `t`. A key that a locale hasn't translated yet falls back to the
 * English key with its {{placeholders}} filled in: next-translate otherwise
 * returns a missing key raw, placeholders and all.
 */
export function useEmployeesT() {
  const { t, lang } = useLocale('ai-employees');
  return {
    lang,
    t: (key: string, query?: Record<string, unknown>) =>
      t(key, query, { default: key }),
  };
}

/** Where every "hire" button on the page leads. */
export const LANDING_PATH = '/ai-employees';

/**
 * The hire link for this visitor: the app for someone signed in, sign-up
 * (which brings them back to the hire flow) for everyone else. With a
 * template, the link opens that role's hire screen; `scratch` opens the
 * screen that drafts a role from a description.
 */
export function useHireHref(template?: string) {
  const { user } = useUser();
  const target =
    template === 'scratch'
      ? '/employees/new'
      : template
        ? `/employees/new?template=${encodeURIComponent(template)}`
        : '/employees';
  if (user) return target;
  if (!template) return signupHrefForPath(LANDING_PATH);
  return `/login?redirectTo=${encodeURIComponent(target)}#auth-sign-up`;
}

/** One role from the hire gallery, slimmed down for the page's props. */
export type RosterEmployee = {
  slug: string;
  name: string;
  role: string;
  category: string;
  tagline: string;
  color: string;
  photo: string;
  photoLarge: string;
  outputs: string[];
  /** Titles of the role's first capability cards, with their emoji. */
  capabilities: { icon: string; title: string }[];
};

export type RosterCategory = { id: string; label: string };

/** A team the onboarding proposes for one kind of work. */
export type TeamPreset = {
  id: string;
  emoji: string;
  label: string;
  description: string;
  /** Template slugs, the Chief of Staff first. */
  members: string[];
};

/**
 * The cast of the page's product mock-ups: real roles from the hire gallery
 * working for Aurela, a fictional small-batch perfumery (the brand in the
 * launch film). Names are people, so they are never translated.
 */
export const BRAND = 'Aurela';
export const CUSTOMER = { name: 'Ana Reyes', company: 'Luma Boutique' };

export type CastMember = {
  name: string;
  role: string;
  photo: string;
  /** Text colour for the role label on dark panels. */
  accent: string;
};

const cast = (
  key: EmployeePhotoKey,
  name: string,
  role: string,
  accent: string,
): CastMember => ({ name, role, photo: employeePhotoUrl(key), accent });

export const CAST = {
  atlas: cast('chief-of-staff', 'Atlas', 'Chief of Staff', 'text-[#a5b4fc]'),
  nova: cast('business-analyst', 'Nova', 'Business Analyst', 'text-[#7dd3fc]'),
  mira: cast('designer', 'Mira', 'Graphic Designer', 'text-[#f9a8d4]'),
  ella: cast(
    'email-marketer',
    'Ella',
    'Email Marketing Specialist',
    'text-[#f0abfc]',
  ),
  maya: cast('support-rep', 'Maya', 'Customer Support Rep', 'text-[#c7d2fe]'),
  leo: cast(
    'marketing-creator',
    'Leo',
    'Marketing Content Creator',
    'text-[#fbcfe8]',
  ),
} satisfies Record<string, CastMember>;

/** Mira's banner for the Bloom launch, made with GPT Image 2.5 Sunburst. */
export const BLOOM_BANNER =
  'https://media.easy-peasy.ai/landing/ai-employees/v1/bloom-banner-960.webp';

export type Status = 'working' | 'done' | 'waiting' | 'idle';

const STATUS_DOT: Record<Status, string> = {
  working: 'bg-primary-400',
  done: 'bg-emerald-400',
  waiting: 'bg-amber-400',
  idle: 'bg-[#8a8aa1]',
};

/** A round portrait, with an optional status dot like the team rail's. */
export function Portrait({
  src,
  size = 32,
  status,
  ringClass = 'ring-[#16151f]',
  className,
}: {
  src: string;
  size?: number;
  status?: Status;
  /** Colour of the gap around the status dot (the surface behind it). */
  ringClass?: string;
  className?: string;
}) {
  const dot = Math.max(8, Math.round(size * 0.28));
  return (
    <span
      className={clsxm('relative inline-flex flex-none', className)}
      style={{ width: size, height: size }}
    >
      <Image
        src={src}
        alt=''
        width={size}
        height={size}
        unoptimized
        className='h-full w-full rounded-full bg-[#2a2938] object-cover'
      />
      {status ? (
        <span
          className={clsxm(
            'absolute -bottom-px -end-px rounded-full ring-2',
            STATUS_DOT[status],
            status === 'working' && 'animate-pulse',
            ringClass,
          )}
          style={{ width: dot, height: dot }}
        />
      ) : null}
    </span>
  );
}

/** An @mention as the group chat draws it (Trans fills in the name). */
export function Mention({ children }: { children?: ReactNode }) {
  return (
    <span className='rounded-md bg-primary-500/20 px-1 py-px font-medium text-primary-200'>
      @{children}
    </span>
  );
}

/**
 * A role's accent colour as text: mixed toward ink on light surfaces and
 * toward white on dark ones, so light accents (amber, emerald) stay readable.
 */
export const ROLE_TEXT =
  'text-[color:color-mix(in_srgb,var(--role)_60%,#10101c)] dark:text-[color:color-mix(in_srgb,var(--role)_62%,white)]';

export const roleColor = (color: string) =>
  ({ '--role': color }) as CSSProperties;

/** The small uppercase label above a section's headline. */
export function Eyebrow({
  children,
  invert = false,
}: {
  children: ReactNode;
  invert?: boolean;
}) {
  return (
    <p
      className={clsxm(
        'mb-5 flex items-center gap-2 text-[13px] font-semibold uppercase tracking-[0.14em]',
        invert ? 'text-primary-200' : 'text-primary-700 dark:text-primary-300',
      )}
    >
      <span className='h-1.5 w-1.5 rounded-full bg-current' />
      {children}
    </p>
  );
}

/** The dark product window every mock-up on the page is drawn in. */
export const WINDOW =
  'overflow-hidden rounded-[22px] bg-[#121219] text-start text-white shadow-[0_40px_90px_-30px_rgba(16,10,70,0.8)] ring-1 ring-white/[0.08]';

/** A light card floating next to a window. */
export const FLOAT_CARD =
  'rounded-2xl bg-white text-start text-[#10101c] shadow-[0_30px_70px_-25px_rgba(8,6,40,0.55)] ring-1 ring-black/5 dark:bg-[#16151f] dark:text-white dark:ring-white/10';
